import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineSearch } from "react-icons/ai";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import Loader from "../components/Layout/Loader";
import ProductCard from "../components/Route/ProductCard/ProductCard";
import styles from "../styles/styles";
import { categoriesData } from "../static/data";
import { fetchSiteOptions } from "../redux/actions/siteOptions";
import { backend_url } from "../server";
import { buildSearchUrl, filterProductsByQuery } from "../utils/productSearch";

const toSlug = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const resolveImageUrl = (value) => {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:")) {
    return value;
  }
  const path = value.startsWith("/") ? value : `/${value}`;
  return `${backend_url}${path}`;
};

const INITIAL_VISIBLE = 24;

const SearchPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFromUrl = searchParams.get("q") || "";

  const { allProducts = [], isLoading } = useSelector((state) => state.products);
  const siteOptionsState = useSelector((state) => state.siteOptions);

  const [inputValue, setInputValue] = useState(queryFromUrl);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    setInputValue(queryFromUrl);
  }, [queryFromUrl]);

  useEffect(() => {
    if (!siteOptionsState?.options?.global) {
      dispatch(fetchSiteOptions("global"));
    }
  }, [dispatch, siteOptionsState?.options?.global]);

  const catalogCategories = useMemo(() => {
    const fromSite = siteOptionsState?.options?.global?.catalog?.categories || [];
    if (Array.isArray(fromSite) && fromSite.length) {
      return fromSite
        .filter((c) => c && c.name && c.isActive !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return categoriesData.map((c) => ({
      name: c.title,
      slug: c.link?.match(/category=([^&]+)/)?.[1],
      image: c.image_Url,
    }));
  }, [siteOptionsState?.options?.global?.catalog?.categories]);

  const suggestions = useMemo(() => {
    const q = inputValue.trim();
    if (q.length < 2) return [];
    return filterProductsByQuery(allProducts, q).slice(0, 6);
  }, [allProducts, inputValue]);

  const results = useMemo(() => {
    if (!queryFromUrl.trim()) return [];
    return filterProductsByQuery(allProducts, queryFromUrl);
  }, [allProducts, queryFromUrl]);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [queryFromUrl, results.length]);

  const submitSearch = useCallback(
    (raw) => {
      const q = (raw ?? inputValue).trim();
      if (!q) {
        navigate("/search", { replace: true });
        return;
      }
      navigate(buildSearchUrl(q));
    },
    [inputValue, navigate]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitSearch();
    }
  };

  const visibleResults = results.slice(0, visibleCount);
  const hasQuery = Boolean(queryFromUrl.trim());

  return (
    <>
      {isLoading && !allProducts.length ? (
        <Loader />
      ) : (
        <div>
          <Header activeHeading={2} />

          <div className="bg-[#CCBEA1] border-t border-t-black" id="inner-page-banner">
            <div className="py-8 flex flex-col px-4 text-center">
              <h1 className="lg:text-4xl text-2xl font-semibold text-slate-900">Search</h1>
              <p className="mt-2 text-sm text-slate-700">
                <Link to="/" className="hover:text-[#38513B]">
                  Home
                </Link>
                {" "}&gt;{" "}
                <span className="text-[#38513B]">Search</span>
                {hasQuery ? (
                  <>
                    {" "}&gt; <span className="text-[#38513B]">{queryFromUrl}</span>
                  </>
                ) : null}
              </p>
            </div>
          </div>

          <div className={`${styles.section} py-6`}>
            <div className="mx-auto max-w-3xl">
              <div className="relative">
                <input
                  type="search"
                  enterKeyHint="search"
                  placeholder="Search for products, brands, categories…"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="h-12 w-full rounded-xl border border-[#38513B] bg-white pl-4 pr-12 text-base text-slate-900 placeholder:text-slate-400 focus:border-[#38513B] focus:outline-none focus:ring-2 focus:ring-[#38513B]/20"
                  autoFocus={typeof window !== "undefined" && window.innerWidth < 800}
                />
                <button
                  type="button"
                  onClick={() => submitSearch()}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-[#38513b]"
                  aria-label="Search"
                >
                  <AiOutlineSearch size={24} />
                </button>
              </div>

              {!hasQuery && inputValue.trim().length >= 2 && suggestions.length > 0 ? (
                <ul className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  {suggestions.map((p) => (
                    <li key={p._id}>
                      <Link
                        to={`/product/${p._id}`}
                        className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0 hover:bg-slate-50"
                      >
                        <img
                          src={
                            p.images?.[0]
                              ? `${backend_url}${p.images[0]}`
                              : "/placeholder.png"
                          }
                          alt=""
                          className="h-10 w-10 shrink-0 object-contain"
                        />
                        <span className="text-sm font-medium text-slate-800 line-clamp-2">
                          {p.name}
                        </span>
                      </Link>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      onClick={() => submitSearch()}
                      className="w-full px-4 py-3 text-left text-sm font-semibold text-[#38513b] hover:bg-slate-50"
                    >
                      View all results for &ldquo;{inputValue.trim()}&rdquo;
                    </button>
                  </li>
                </ul>
              ) : null}
            </div>

            {hasQuery ? (
              <div className="mt-8">
                <p className="mb-4 text-sm text-slate-600">
                  {results.length === 0
                    ? `No listings found for “${queryFromUrl}”.`
                    : `${results.length} listing${results.length === 1 ? "" : "s"} for “${queryFromUrl}”`}
                </p>
                {results.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                      {visibleResults.map((product) => (
                        <ProductCard key={product._id} data={product} isEvent={false} />
                      ))}
                    </div>
                    {visibleCount < results.length ? (
                      <div className="mt-8 flex justify-center">
                        <button
                          type="button"
                          onClick={() => setVisibleCount((n) => n + INITIAL_VISIBLE)}
                          className="rounded-xl border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-[#38513b] shadow-sm hover:border-[#38513b]"
                        >
                          Load more
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}

            <section className={hasQuery ? "mt-10 border-t border-slate-200 pt-8" : "mt-8"}>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Browse categories</h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                <Link
                  to="/products/all"
                  className="group block rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-[#38513b] hover:shadow-md transition"
                >
                  <div className="aspect-square w-full rounded-lg mb-3 flex items-center justify-center bg-gradient-to-br from-[#f3f7f3] to-[#e8efe9] border border-[#38513b]/15">
                    <span className="text-3xl font-semibold text-[#38513b]">All</span>
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 group-hover:text-[#38513b]">
                    All products
                  </h3>
                </Link>
                {catalogCategories.map((cat) => {
                  const slug = toSlug(cat.slug || cat.name);
                  const href = `/products/${slug}`;
                  const imgSrc = resolveImageUrl(cat.image || cat.icon || "");
                  return (
                    <Link
                      key={cat._id || slug || cat.name}
                      to={href}
                      className="group block rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-[#38513b] hover:shadow-md transition"
                    >
                      <div className="aspect-square w-full rounded-lg overflow-hidden mb-3 bg-slate-50">
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={cat.name}
                            className="h-full w-full object-contain group-hover:scale-105 transition"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-3xl font-light text-slate-400">
                            {(cat.name || "?").charAt(0)}
                          </div>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 group-hover:text-[#38513b] line-clamp-2">
                        {cat.name || cat.title}
                      </h3>
                    </Link>
                  );
                })}
              </div>
            </section>
          </div>

          <Footer />
        </div>
      )}
    </>
  );
};

export default SearchPage;
